import { useEffect } from 'react';

import { NOTIFICATION_REFRESH_INTERVAL_MS } from '../../ducks/user.duck';

const TICK_MS = 60 * 1000;
const ACTIVITY_EVENTS = ['pointerdown', 'pointermove', 'keydown', 'scroll', 'touchstart'];

/**
 * Keep unread conversation counts reasonably fresh while the user is actually using the page.
 * Every trigger is cheap: the notifications thunk skips the API calls until the previous
 * refresh is NOTIFICATION_REFRESH_INTERVAL_MS old. Visible but unattended tabs (e.g. a screen
 * left open) stop polling until the user interacts again.
 *
 * @param {boolean} enabled whether there is a signed-in user
 * @param {Function} onRefresh dispatches fetchCurrentUserNotifications and returns a promise
 */
export const useNotificationRefresh = (enabled, onRefresh) => {
  useEffect(() => {
    if (!enabled || !onRefresh) return;
    let lastActivityAt = Date.now();
    let lastAttemptAt = 0;

    const refresh = () => {
      if (document.visibilityState !== 'visible') return;
      lastAttemptAt = Date.now();
      onRefresh().catch(() => {});
    };
    const onTick = () => {
      if (Date.now() - lastActivityAt < NOTIFICATION_REFRESH_INTERVAL_MS) refresh();
    };
    const onActivity = () => {
      lastActivityAt = Date.now();
      if (lastActivityAt - lastAttemptAt >= TICK_MS) refresh();
    };

    ACTIVITY_EVENTS.forEach(e => window.addEventListener(e, onActivity, { passive: true }));
    window.addEventListener('focus', refresh);
    const timer = window.setInterval(onTick, TICK_MS);
    return () => {
      ACTIVITY_EVENTS.forEach(e => window.removeEventListener(e, onActivity));
      window.removeEventListener('focus', refresh);
      window.clearInterval(timer);
    };
  }, [enabled, onRefresh]);
};
